import {
	faCakeCandles,
	faClose,
	faSquare,
	faSquareCheck,
	faUserGroup,
} from "@fortawesome/pro-regular-svg-icons";

import { Link } from "@mui/material";

import { FunctionComponent, useState } from "react";

import { Tooltip } from "@/components/Tooltip/Tooltip";

import { LinkedinIcon } from "@/components/LinkedinIcon/LinkedinIcon";

import { ExternalLink } from "@/components/ExternalLink/ExternalLink";

import { Icon } from "@/components";
import { TableColumn } from "@/components/Table/Table";

import { SortParams } from "@/types/types";

import { classNames } from "@/utils/classNames";
import { ensureHttpProtocol } from "@/utils/url";
import { Checkbox } from "@/components/Checkbox/Checkbox";

import { Button } from "@/components/Button/Button";

import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { HideOnErrorImg } from "@/components/HideOnErrorImg/HideOnErrorImg";

import { getOrderStatus } from "../../List/utils/column-utils";
import { ListPerson } from "../../List/schema/list";
import { TrackerOrganization } from "../schema/trackerOrganizations";
import { TrackerCompanyScoreEditor } from "../components/TrackerCompanyScoreEditor";
import { DeepResearchStatusCell } from "../components/CustomColumns/DeepResearchStatusCell";
import { TrackerSignalCell } from "../components/CustomColumns/TrackerSignalCell";
import { LeadColorDots } from "../../List/components/CampaignOverview/components/Lead/components/LeadColorDots";

export const renderSignalColumn = (
	ts: (key: string) => string,
	signalsConfigured: boolean
): TableColumn => {
	return {
		title: ts("signal") || "Signal",
		width: "fit-content",
		minWidth: "200px",
		uid: "signal",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: TrackerOrganization) => (
			<TrackerSignalCell row={row} signalsConfigured={signalsConfigured} />
		),
	};
};

export const renderEstimatedNumEmployeesColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "organization.estimatedNumEmployees";

	return {
		title: ts("companyEmployees"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "companyEmployees",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: TrackerOrganization) => {
			const estimatedNumEmployees =
				row.organization?.estimatedNumEmployees || row.holdingOrganization?.estimatedNumEmployees;

			return (
				<div className="flex flex-row">
					<button
						className="cursor-pointer mr-2 flex flex-row items-center gap-2"
						onClick={() =>
							estimatedNumEmployees && handleCopyClick(estimatedNumEmployees.toString())
						}
					>
						{estimatedNumEmployees ? (
							<>
								{estimatedNumEmployees} <Icon color="##374151" icon={faUserGroup} />
							</>
						) : (
							"-"
						)}
					</button>
				</div>
			);
		},
	};
};

export const renderReportedEmployeesColumn = (
	_ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "organization.employeesFrom";

	return {
		title: "Reported employees",
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "reportedEmployees",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: TrackerOrganization) => {
			const employeesFrom =
				row.organization?.employeesFrom || row.holdingOrganization?.employeesFrom;
			const employeesTo = row.organization?.employeesTo || row.holdingOrganization?.employeesTo;

			const displayValue =
				employeesFrom && employeesTo && employeesTo !== employeesFrom
					? `${employeesFrom} - ${employeesTo}`
					: employeesFrom?.toString() || "";

			return (
				<div className="flex flex-row">
					<button
						className="cursor-pointer mr-2"
						onClick={(e) => {
							e.stopPropagation();
							e.preventDefault();
							displayValue && handleCopyClick(displayValue);
						}}
					>
						{displayValue || "-"}
					</button>
				</div>
			);
		},
	};
};

export const renderFoundedYearColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "organization.foundedYear";

	return {
		title: ts("foundedYear"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "foundedYear",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ListPerson) => (
			<div className="flex flex-row">
				<button
					className="cursor-pointer mr-2"
					onClick={() =>
						row.prospect?.organization?.foundedYear &&
						handleCopyClick(row.prospect?.organization?.foundedYear?.toString() || "")
					}
				>
					{row.prospect?.organization?.foundedYear}{" "}
					{row.prospect?.organization?.foundedYear && (
						<Icon color="##374151" icon={faCakeCandles} />
					)}
				</button>
			</div>
		),
	};
};

export const renderIndustryColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "organization.industry";

	return {
		title: ts("industry"),
		width: "fit-content",
		orderTitle: "organization.industry",
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "industry",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: TrackerOrganization) => {
			const industry =
				row.organization?.industry?.capitalizeString() ||
				row.holdingOrganization?.industry?.capitalizeString() ||
				"";

			return (
				<div>
					<button className="cursor-pointer" onClick={() => industry && handleCopyClick(industry)}>
						{industry || "-"}
					</button>
				</div>
			);
		},
	};
};

export const renderTrackerPersonsColumn = (
	ts: (key: string) => string,
	onPersonClick?: (trackerOrganizationId: number, personId: number) => void
): TableColumn => {
	return {
		title: ts("persons"),
		width: "fit-content",
		minWidth: "150px",
		uid: "trackerPersons",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: TrackerOrganization) => (
			<TrackerPersonsAvatars
				trackerOrganizationId={row.id}
				trackerPersons={row.trackerPersons}
				onPersonClick={onPersonClick}
			/>
		),
	};
};

const TrackerPersonsAvatars: FunctionComponent<{
	trackerPersons?: TrackerOrganization["trackerPersons"];
	trackerOrganizationId: number;
	onPersonClick?: (trackerOrganizationId: number, personId: number) => void;
}> = ({ trackerPersons, trackerOrganizationId, onPersonClick }) => {
	if (!trackerPersons || trackerPersons.length === 0) {
		return <span className="text-gray-400">-</span>;
	}

	const maxVisible = 5;
	const visiblePersons = trackerPersons.slice(0, maxVisible);
	const remainingCount = trackerPersons.length - maxVisible;

	return (
		<div className="flex items-center -space-x-2">
			{visiblePersons.map((person) => (
				<PersonAvatar
					key={person.id}
					person={person}
					trackerOrganizationId={trackerOrganizationId}
					onPersonClick={onPersonClick}
				/>
			))}
			{remainingCount > 0 && (
				<div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 text-gray-600 text-xs font-medium border-2 border-white z-10">
					+{remainingCount}
				</div>
			)}
		</div>
	);
};

const PersonAvatar: FunctionComponent<{
	person: NonNullable<TrackerOrganization["trackerPersons"]>[number];
	trackerOrganizationId: number;
	onPersonClick?: (trackerOrganizationId: number, personId: number) => void;
}> = ({ person, trackerOrganizationId, onPersonClick }) => {
	const firstName = person.prospect?.firstName || person.name?.split(" ")[0] || "";
	const lastName = person.prospect?.lastName || person.name?.split(" ")[1] || "";
	const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
	const imageUrl = person.prospect?.image;

	const [imageError, setImageError] = useState(false);

	const fullName = person.prospect?.name || person.name || "Unknown";

	const handleClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		e.preventDefault();
		if (onPersonClick && person.prospect?.id) {
			onPersonClick(trackerOrganizationId, person.prospect.id);
		}
	};

	return (
		<Tooltip content={fullName}>
			<div
				className={classNames("relative", onPersonClick ? "cursor-pointer hover:z-20" : "")}
				onClick={handleClick}
			>
				{imageUrl && !imageError ? (
					<img
						alt={fullName}
						className={classNames(
							"w-8 h-8 rounded-full border-2 border-white object-cover",
							onPersonClick ? "hover:border-blue-action hover:scale-110 transition-transform" : ""
						)}
						referrerPolicy="no-referrer"
						src={imageUrl}
						onError={() => setImageError(true)}
					/>
				) : (
					<div
						className={classNames(
							"flex items-center justify-center w-8 h-8 rounded-full text-white text-xs font-medium border-2 border-white bg-blue-action",
							onPersonClick ? "hover:border-blue-600 hover:scale-110 transition-transform" : ""
						)}
					>
						{initials || "?"}
					</div>
				)}
			</div>
		</Tooltip>
	);
};

export const renderCompanyColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams,
	setShowEdit: ({ id, name }: { id: number; name: string }) => void,
	showEdit: { id: number; name: string } | undefined
): TableColumn => {
	const orderTitle = "organization.name";

	return {
		title: ts("company"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		minWidth: "220px",
		uid: "company",
		hideable: true,
		locked: "profileTrackerLeftSecond",
		moveable: false,
		show: true,
		render: (row: TrackerOrganization) => (
			<EditableCompanyColumn
				{...{
					row,
					handleCopyClick,
					orderTitle,
					setShowEdit,
					showEdit,
					sortingInbox,
				}}
			/>
		),
	};
};

export const EditableCompanyColumn: FunctionComponent<{
	row: TrackerOrganization;
	handleCopyClick: (value: string) => void;
	setShowEdit: ({ id, name }: { id: number; name: string }) => void;
	showEdit: { id: number; name: string } | undefined;
	orderTitle: string;
}> = ({ row, handleCopyClick, orderTitle, setShowEdit }) => {
	function truncate(str: string, n: number) {
		return str.length > n ? str.slice(0, n - 1) + "..." : str;
	}

	return (
		<div className="flex w-full max-w-[220px] items-center">
			<TrackerCompanyScoreEditor
				companyResult={row?.scoreCompanyCategoryResult}
				organizationId={row.organization?.id}
			/>
			<HideOnErrorImg
				alt="logo"
				className="max-h-[24px] h-full w-[24px] rounded mr-1"
				referrerPolicy="no-referrer"
				src={row.organization?.logoUrl}
			/>
			<div className="ml-2 w-full flex flex-row justify-between">
				<Tooltip
					content={
						<div className="flex flex-col min-w-[300px] text-sm">
							<div>
								<span className="font-bold">Name:</span>
								{row.name || row.organization.name || ""}
							</div>
							<div>
								<span className="font-bold">Followers:</span>
								{row.organization?.followerCount || 0}
							</div>
							<div>
								<span className="font-bold">Short Description:</span>
								<span
									className="leading-6 truncate ..."
									style={{
										textWrap: "wrap",
									}}
								>
									{truncate(row.organization?.shortDescription ?? "", 180)}
								</span>
							</div>
						</div>
					}
				>
					<button
						className="cursor-pointer mr-1  max-w-[100px] text-ellipsis overflow-hidden"
						onClick={() => handleCopyClick(row.name || "")}
					>
						<span
							onContextMenu={(e) => {
								e.preventDefault(); // prevent the default behaviour when right clicked
								setShowEdit({ id: row.id, name: orderTitle });
							}}
						>
							{row.name || row.organization.name || ""}
						</span>
					</button>
				</Tooltip>

				<div className="flex items-center">
					{row.organization?.linkedinUrl && (
						<Link href={ensureHttpProtocol(row.organization?.linkedinUrl)} target="_blank">
							<LinkedinIcon status="checked" />
						</Link>
					)}
					{row.organization?.websiteUrl && (
						<Link href={ensureHttpProtocol(row.organization?.websiteUrl)} target="_blank">
							<ExternalLink />
						</Link>
					)}
				</div>
			</div>
		</div>
	);
};

export const EditableHoldingCompanyColumn: FunctionComponent<{
	row: TrackerOrganization;
	handleCopyClick: (value: string) => void;
	setShowEdit: ({ id, name }: { id: number; name: string }) => void;
	setShowDelete: ({ id, name }: { id: number; name: string }) => void;
	showEdit: { id: number; name: string } | undefined;
	orderTitle: string;
}> = ({ row, handleCopyClick, orderTitle, setShowEdit, setShowDelete }) => {
	function truncate(str: string, n: number) {
		return str.length > n ? str.slice(0, n - 1) + "..." : str;
	}

	return (
		<div className="flex w-full max-w-[220px] items-center">
			<HideOnErrorImg
				alt="logo"
				className="max-h-[24px] h-full w-[24px] rounded mr-1"
				referrerPolicy="no-referrer"
				src={row.holdingOrganization?.logoUrl}
			/>
			<div className="ml-2 w-full flex flex-row justify-between group">
				{row.holdingOrganization ? (
					<Tooltip
						content={
							<div className="flex flex-col min-w-[300px] text-sm">
								<div>
									<span className="font-bold">Name:</span>
									{row.holdingOrganization?.name || ""}
								</div>
								<div>
									<span className="font-bold">Followers:</span>
									{row.holdingOrganization?.followerCount || 0}
								</div>
								<div>
									<span className="font-bold">Short Description:</span>
									<span
										className="leading-6 truncate ..."
										style={{
											textWrap: "wrap",
										}}
									>
										{truncate(row.holdingOrganization?.shortDescription ?? "", 180)}
									</span>
								</div>
							</div>
						}
					>
						<button
							className="cursor-pointer mr-1  max-w-[100px] text-ellipsis overflow-hidden"
							onClick={() => handleCopyClick(row.name || "")}
						>
							<span
								onContextMenu={(e) => {
									e.preventDefault(); // prevent the default behaviour when right clicked

									setShowEdit({ id: row.id, name: orderTitle });
								}}
							>
								{row.holdingOrganization?.name}
							</span>
						</button>
					</Tooltip>
				) : (
					<button
						className="cursor-pointer mr-1 min-w-[100px]  w-full text-ellipsis text-left overflow-hidden group "
						onClick={(e) => {
							e.preventDefault(); // prevent the default behaviour when right clicked
							e.stopPropagation();
							setShowEdit({ id: row.id, name: orderTitle });
						}}
					>
						<span className="group-hover:hidden text-gray-200">{"none"}</span>
						<div className="hidden group-hover:flex w-auto">
							<Button
								color={ButtonColor.ACTION_SECONDARY}
								size={ButtonSize.XS}
								title="Click to set holding company"
							/>
						</div>
					</button>
				)}

				<div className="flex items-center">
					{row.holdingOrganization?.linkedinUrl && (
						<Link href={ensureHttpProtocol(row.holdingOrganization?.linkedinUrl)} target="_blank">
							<LinkedinIcon status="checked" />
						</Link>
					)}
					{row.holdingOrganization?.websiteUrl && (
						<Link href={ensureHttpProtocol(row.holdingOrganization?.websiteUrl)} target="_blank">
							<ExternalLink />
						</Link>
					)}
					{row.holdingOrganization && (
						<div className="hidden group-hover:flex">
							<Icon
								className="cursor-pointer mr-2 w-[20px] h-[20px]"
								icon={faClose}
								onClick={(e: MouseEvent) => {
									e.preventDefault();
									e.stopPropagation();

									setShowDelete({ id: row.id, name: row.holdingOrganization?.name || "" });
								}}
							/>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export const renderSelectColumn = (
	toggleSelectAll: () => void,
	selectAll: boolean,
	selectedItems: number[],
	handleSelectItem: (id: number) => void
): TableColumn => {
	return {
		title: "",
		uid: "select",
		icon: selectAll ? faSquareCheck : faSquare,
		allowExclude: false,
		locked: "profileTrackerLeftFirst",
		hideable: false,
		moveable: false,
		show: true,
		onHeaderClick: () => {
			toggleSelectAll();
		},
		render: (row: TrackerOrganization, index?: number) => (
			<div className="group flex items-center">
				<div
					className={classNames(
						"w-[16px] text-center font-medium text-table-header",
						selectedItems?.find((lf) => lf === row.id) ? "hidden" : "group-hover:hidden"
					)}
				>
					{typeof index !== "undefined" ? index + 1 : ""}
				</div>
				<Checkbox
					className={classNames(
						selectedItems?.find((lf) => lf === row.id) ? "block" : "hidden group-hover:block"
					)}
					isChecked={!!selectedItems?.find((lf) => lf === row.id)}
					name={"selectedProspects"}
					onChange={(e) => {
						e.stopPropagation();
						e.preventDefault();

						handleSelectItem(row.id);
					}}
				/>
			</div>
		),
	};
};

export const renderCompanyCityColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "organization.city";

	return {
		title: ts("companyCity"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "city",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: TrackerOrganization) => (
			<div className="flex flex-row">
				<button
					className="cursor-pointer"
					type="button"
					onClick={() =>
						row.organization?.city ||
						(row.holdingOrganization?.city &&
							handleCopyClick(row.organization?.city || row.holdingOrganization.city))
					}
				>
					{row.organization?.city || row.holdingOrganization?.city || ""}
				</button>
			</div>
		),
	};
};

export const renderCompanyStateColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "organization.state";

	return {
		title: ts("companyState"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "state",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: TrackerOrganization) => {
			const state = row.organization?.state || row.holdingOrganization?.state || "";

			return (
				<div className="flex flex-row">
					<button
						className="cursor-pointer"
						type="button"
						onClick={() => state && handleCopyClick(state)}
					>
						{state || "-"}
					</button>
				</div>
			);
		},
	};
};

export const renderCompanyCountryColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "organization.country";

	return {
		title: ts("companyCountry"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "country",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: TrackerOrganization) => (
			<div className="flex flex-row">
				<button
					className="cursor-pointer"
					type="button"
					onClick={() =>
						row.organization?.country ||
						(row.holdingOrganization?.country &&
							handleCopyClick(row.organization?.country || row.holdingOrganization.country))
					}
				>
					{row.organization?.country || row.holdingOrganization?.country || ""}
				</button>
			</div>
		),
	};
};

export const renderScoreColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "score";

	return {
		title: ts("score"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "score",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: TrackerOrganization) => {
			const score = row.columns.reduce((acc, column) => {
				const stringValue =
					typeof column.value?.answer === "string" ? (column.value?.answer as string) : "";
				const numberValue =
					typeof column.value?.answer === "number" ? (column.value?.answer as number) : 0;

				if (
					column.value?.answer &&
					((stringValue && stringValue.toLowerCase() !== "no") || (numberValue && numberValue > 0))
				) {
					acc += column?.columnConfig?.score || 0;
				}

				return acc;
			}, 0);

			return (
				<div className="flex flex-row">
					<button
						className="cursor-pointer"
						type="button"
						onClick={() => score && handleCopyClick(score.toString())}
					>
						{score}
					</button>
				</div>
			);
		},
	};
};

export const renderCompanyCategoryColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "scoreCompanyCategoryResult.category";

	return {
		title: ts("companyCategory") || "Company category",
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "companyCategory",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: TrackerOrganization) => {
			const category =
				row.scoreCompanyCategoryResult?.scoreCompanyCategory?.category ||
				row.scoreCompanyCategoryResult?.result ||
				"";

			return (
				<div className="flex flex-row">
					<button
						className="cursor-pointer"
						type="button"
						onClick={() => category && handleCopyClick(category)}
					>
						{category || "-"}
					</button>
				</div>
			);
		},
	};
};

export const renderDeepResearchStatusColumn = (
	onResearchClick?: (row: TrackerOrganization) => void
): TableColumn => {
	return {
		title: "Research",
		width: "fit-content",
		minWidth: "60px",
		uid: "deepResearchStatus",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: TrackerOrganization) => (
			<DeepResearchStatusCell row={row} onResearchClick={onResearchClick} />
		),
	};
};
